import { useCallback, useEffect, useMemo, useState } from 'react';
import { Inbox, Search, UserRound, UsersRound, CheckCircle2, RefreshCw, Mail, ListFilter, SlidersHorizontal, UserCheck, CircleDot, XCircle, RotateCcw, X, CheckSquare2, Plus } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, dateTime } from '../lib/api';
import CaseTypeBadge from '../components/CaseTypeBadge';

const QUEUES=[
  {key:'triage',label:'Triage',icon:ListFilter},
  {key:'all_open',label:'All open',icon:Inbox},
  {key:'assigned_to_me',label:'Assigned to me',icon:UserRound},
  {key:'unassigned',label:'Unassigned',icon:UsersRound},
  {key:'resolved',label:'Resolved',icon:CheckCircle2},
];

const readable=value=>String(value||'—').replaceAll('_',' ').replace(/\b\w/g,m=>m.toUpperCase());

export default function RequestQueues(){
  const navigate=useNavigate(),[searchParams,setSearchParams]=useSearchParams();
  const initialQueue=QUEUES.some(item=>item.key===searchParams.get('queue'))?searchParams.get('queue'):'triage';
  const[queue,setQueue]=useState(initialQueue);
  const[data,setData]=useState({cases:[],counts:{}});
  const[query,setQuery]=useState('');
  const[status,setStatus]=useState('');
  const[type,setType]=useState('');
  const[assignee,setAssignee]=useState('');
  const[loading,setLoading]=useState(true);
  const[syncing,setSyncing]=useState(false);
  const[syncNotice,setSyncNotice]=useState(null);
  const[error,setError]=useState('');
  const[selected,setSelected]=useState([]);
  const[bulkBusy,setBulkBusy]=useState(false);
  const[bulkNotice,setBulkNotice]=useState('');

  const load=useCallback(async()=>{
    setLoading(true);setError('');
    try{
      const params=new URLSearchParams({queue});
      if(query.trim())params.set('q',query.trim());
      if(status)params.set('status',status);
      if(type)params.set('type',type);
      if(assignee)params.set('assignee',assignee);
      const body=await api('/api/cases?'+params.toString());
      setData(body);
    }catch(e){setError(e.message)}
    finally{setLoading(false)}
  },[queue,query,status,type,assignee]);

  const syncMicrosoft=useCallback(async()=>{
    setSyncing(true);setError('');setSyncNotice(null);
    try{
      const result=await api('/api/email/intake/sync',{
        method:'POST',
        headers:{'content-type':'application/json'},
        body:'{}',
      });
      const intake=result?.intake||{};
      setSyncNotice({
        message:result?.message||'Microsoft 365 sync completed.',
        contactMailboxes:result?.contact_mailboxes||[],
        synced:Number(result?.contact_sync_message_count||0),
        eligible:Number(intake.eligible||0),
        created:Number(intake.created||0),
        triaged:Number(intake.triaged||0),
        acknowledgements:Number(intake.sent||0),
        requestErrors:Array.isArray(intake.request_errors)?intake.request_errors:[],
        notificationErrors:Array.isArray(intake.notification_errors)?intake.notification_errors:[],
      });
      await load();
      return result;
    }catch(e){setError(e.message)}
    finally{setSyncing(false)}
  },[load]);

  useEffect(()=>{const timer=setTimeout(()=>void load(),query?250:0);return()=>clearTimeout(timer)},[load,query]);

  const chooseQueue=key=>{
    setSelected([]);setBulkNotice('');
    setQueue(key);
    const next=new URLSearchParams(searchParams);
    if(key==='triage')next.delete('queue');else next.set('queue',key);
    setSearchParams(next,{replace:true});
  };
  const active=QUEUES.find(item=>item.key===queue)||QUEUES[0];
  const ActiveIcon=active.icon;
  const rows=useMemo(()=>data.cases||[],[data.cases]);
  const allSelected=rows.length>0&&rows.every(row=>selected.includes(row.id));
  const toggleOne=id=>setSelected(current=>current.includes(id)?current.filter(value=>value!==id):[...current,id]);
  const toggleAll=()=>setSelected(allSelected?[]:rows.map(row=>row.id));
  const runBulk=async action=>{
    if(!selected.length)return;
    if(action==='close'&&!window.confirm(`Close ${selected.length} selected work item${selected.length===1?'':'s'}?`))return;
    setBulkBusy(true);setBulkNotice('');setError('');
    try{
      const result=await api('/api/cases/bulk',{method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify({ids:selected,action})});
      setBulkNotice(`${result.succeeded||0} selected work item${Number(result.succeeded||0)===1?'':'s'} updated.`);
      setSelected([]);
      await load();
    }catch(e){setError(e.message)}
    finally{setBulkBusy(false)}
  };

  return <div className="govuk-grid-row">
    <aside className="govuk-grid-column-one-quarter">
      <div className="govuk-!-margin-bottom-6">
        <span className="govuk-caption-m">Customer Operations</span>
        <h2 className="govuk-heading-m">Queues</h2>
      </div>
      <nav aria-label="Caseworking queues">
        {QUEUES.map(item=>{
          const Icon=item.icon;
          const count=Number(data.counts?.[item.key]||0);
          return <button key={item.key} className={queue===item.key?'govuk-link govuk-!-font-weight-bold':'govuk-link'} aria-current={queue===item.key?'page':undefined} type="button" onClick={()=>chooseQueue(item.key)}>
            <span><Icon size={15}/>{item.label}</span>
            {count>0&&<strong>{count}</strong>}
          </button>;
        })}
      </nav>
      <div className="govuk-inset-text">
        <Mail size={14}/>
        <span><strong>Email stays in Inbox</strong><small>Requests are linked work items created from contact channels or by staff on behalf of customers.</small></span>
      </div>
    </aside>

    <section className="govuk-grid-column-three-quarters">
      <div className="govuk-caption-m">Customer Operations <span>/</span> Cases <span>/</span> Queues</div>
      <header className="govuk-!-margin-bottom-6">
        <div>
          <h1 className="govuk-heading-xl">{active.label}</h1>
          <p className="govuk-body">{queue==='triage'?'New cases and work items from customer contact channels or staff-assisted intake waiting for triage and assignment.':queue==='assigned_to_me'?'Cases and work items currently assigned to you.':queue==='unassigned'?'Open cases and work items waiting for an owner.':queue==='resolved'?'Resolved and closed customer cases and work items.':'All unresolved customer cases and work items requiring Customer Operations attention.'}</p>
        </div>
        <div className="govuk-button-group">
          <button className="govuk-button govuk-button--secondary" onClick={()=>navigate('/requests/new')}><Plus size={14}/>Create work item</button>
          <button className="govuk-button govuk-button--secondary" onClick={syncMicrosoft} disabled={syncing||loading}><Mail size={14}/>{syncing?'Syncing…':'Sync Microsoft 365'}</button>
          <button className="govuk-button govuk-button--secondary" onClick={load} disabled={loading||syncing}><RefreshCw size={14}/>{loading?'Refreshing…':'Refresh'}</button>
        </div>
      </header>

      {syncNotice&&<div className="govuk-notification-banner">
        <div><strong>{syncNotice.message}</strong><span>{syncNotice.contactMailboxes.length?'Contact mailbox: '+syncNotice.contactMailboxes.join(', '):'Contact mailbox checked.'}</span></div>
        <div className="request-sync-metrics">
          <span><b>{syncNotice.synced}</b> mailbox messages</span>
          <span><b>{syncNotice.eligible}</b> eligible</span>
          <span><b>{syncNotice.created}</b> new requests</span>
          <span><b>{syncNotice.acknowledgements}</b> acknowledgements</span>
        </div>
        {!!syncNotice.requestErrors.length&&<small>Request error: {syncNotice.requestErrors[0].message}</small>}
        {!!syncNotice.notificationErrors.length&&<small>Acknowledgement warning: {syncNotice.notificationErrors[0].message}</small>}
      </div>}

      <p className="govuk-caption-m govuk-!-margin-bottom-2">List view</p>
      <div className="govuk-form-group cw-queue-filters">
        <div className="cw-queue-search"><label className="govuk-label" htmlFor="work-items-search">Search work items</label><input id="work-items-search" className="govuk-input" value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search work" aria-label="Search work items"/></div>
        <select className="govuk-select govuk-!-margin-right-3" aria-label="Filter by status" value={status} onChange={e=>setStatus(e.target.value)}>
          <option value="">Status</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="waiting_for_customer">Waiting for customer</option>
          <option value="waiting_for_support">Waiting for support</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select className="govuk-select govuk-!-margin-right-3" aria-label="Filter by type" value={type} onChange={e=>setType(e.target.value)}>
          <option value="">Type</option>
          <option value="request">Request</option>
          <option value="question">Question</option>
          <option value="complaint">Complaint</option>
          <option value="task">Task</option>
          <option value="incident">Service issue</option>
          <option value="feedback">Feedback</option>
        </select>
        <select className="govuk-select govuk-!-margin-right-3" aria-label="Filter by assignee" value={assignee} onChange={e=>setAssignee(e.target.value)}>
          <option value="">Assignee</option>
          <option value="me">Assigned to me</option>
          <option value="unassigned">Unassigned</option>
        </select>
        <button className="govuk-button govuk-button--secondary" type="button"><SlidersHorizontal size={14}/>More filters</button>
      </div>

      <div className="govuk-body-s">{rows.length} work item{rows.length===1?'':'s'}</div>
      {bulkNotice&&<div className="govuk-notification-banner" role="status">{bulkNotice}</div>}
      {!!rows.length&&(selected.length
        ?<div className="govuk-button-group govuk-!-margin-bottom-5">
          <div className="request-bulk-selection"><CheckSquare2 size={15}/><strong>{selected.length} selected</strong></div>
          <div className="request-bulk-actions">
            <button type="button" onClick={()=>runBulk('assign_to_me')} disabled={bulkBusy}><UserCheck size={14}/>Assign to me</button>
            <button type="button" onClick={()=>runBulk('mark_in_progress')} disabled={bulkBusy}><CircleDot size={14}/>Mark in progress</button>
            <button type="button" onClick={()=>runBulk('resolve')} disabled={bulkBusy}><CheckCircle2 size={14}/>Resolve</button>
            <button type="button" className="danger" onClick={()=>runBulk('close')} disabled={bulkBusy}><XCircle size={14}/>Close</button>
            <button type="button" onClick={()=>runBulk('reopen')} disabled={bulkBusy}><RotateCcw size={14}/>Reopen</button>
            <button type="button" className="quiet" onClick={()=>setSelected([])} disabled={bulkBusy}><X size={14}/>Clear selection</button>
          </div>
        </div>
        :<div className="govuk-inset-text"><CheckSquare2 size={14}/><span>Select one or more work items to use bulk actions</span></div>)}

      {error?<div className="govuk-error-summary" role="alert"><strong>Cases unavailable</strong><span>{error}</span></div>:
      loading&&!rows.length?<div className="govuk-inset-text"><span>Loading case queue…</span></div>:
      !rows.length?<div className="govuk-inset-text"><ActiveIcon size={40}/><h3 className="govuk-heading-s">Your queue is empty</h3><p className="govuk-body">Incoming cases and work items created from contact channels or by staff on behalf of customers will appear here ready to be triaged and assigned.</p></div>:
      <div className="govuk-!-margin-top-4" style={{overflowX:"auto"}}>
        <table className="govuk-table">
          <thead className="govuk-table__head"><tr className="govuk-table__row"><th className="govuk-table__header" scope="col"><input type="checkbox" aria-label="Select all visible requests" checked={allSelected} onChange={toggleAll}/></th><th className="govuk-table__header" scope="col">Type</th><th className="govuk-table__header" scope="col">Key</th><th className="govuk-table__header" scope="col">Summary</th><th className="govuk-table__header" scope="col">Reporter</th><th className="govuk-table__header" scope="col">Assignee</th><th className="govuk-table__header" scope="col">Status</th><th className="govuk-table__header" scope="col">Created</th><th className="govuk-table__header" scope="col">Time to resolution</th></tr></thead>
          <tbody className="govuk-table__body">{rows.map(row=><tr className="govuk-table__row" key={row.id} onClick={()=>navigate('/cases/'+encodeURIComponent(row.id))}>
            <td className="govuk-table__cell" onClick={e=>e.stopPropagation()}><input type="checkbox" checked={selected.includes(row.id)} onChange={()=>toggleOne(row.id)} aria-label={'Select '+(row.case_reference||'request')}/></td>
            <td className="govuk-table__cell"><CaseTypeBadge row={row} compact/></td>
            <td className="govuk-table__cell"><span className="govuk-!-font-weight-bold">{row.case_reference||'—'}</span>{Number(row.email_origin)===1&&<span className="request-origin"><Mail size={10}/>Email</span>}</td>
            <td className="govuk-table__cell"><strong className="govuk-link">{row.title||'Untitled request'}</strong></td>
            <td className="govuk-table__cell"><span className="govuk-body-s">{row.reporter_name||row.customer_name||'Unknown reporter'}</span>{row.email_reporter_email&&<small>{row.email_reporter_email}</small>}</td>
            <td className="govuk-table__cell">{row.owner_name?<span className="govuk-body-s">{row.owner_name}</span>:<span className="govuk-body-s"><UserRound size={14}/>Unassigned</span>}</td>
            <td className="govuk-table__cell"><span className={'govuk-tag status-'+String(row.status||'open').toLowerCase()}>{readable(row.status||'open')}</span></td>
            <td className="govuk-table__cell">{dateTime(row.created_at)}</td>
            <td className="govuk-table__cell">{row.due_at?<span className={new Date(row.due_at).getTime()<Date.now()&&!['resolved','closed'].includes(String(row.status||'').toLowerCase())?'request-due overdue':'request-due'}>{dateTime(row.due_at)}</span>:<span className="muted-line">—</span>}</td>
          </tr>)}</tbody>
        </table>
      </div>}
    </section>
  </div>;
}
